import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { API_CONFIG } from '../config/api';
import axios from 'axios';

interface Client {
  id: number;
  name: string;
  created_at: string;
  current_plan?: string;
  plan_status?: string;
  available_credits?: number;
  has_active_plan?: boolean;
}

interface PaginationInfo {
  current_page: number;
  total_pages: number;
  total_count: number;
  per_page: number;
  has_next_page: boolean;
  has_prev_page: boolean;
}

interface SearchInfo {
  query: string;
  results_count: number;
}

interface ClientContextType {
  selectedClient: Client | null;
  clients: Client[];
  loading: boolean;
  error: string | null;
  pagination: PaginationInfo | null;
  searchInfo: SearchInfo | null;
  hasFetched: boolean;
  selectClient: (client: Client) => void;
  clearSelectedClient: () => void;
  fetchClients: (page?: number, query?: string) => Promise<void>;
  searchClients: (query: string) => Promise<void>;
  loadMoreClients: () => Promise<void>;
  retryFetchClients: () => Promise<void>;
  resetContext: () => void;
}

const ClientContext = createContext<ClientContextType | undefined>(undefined);

export const useClient = () => {
  const context = useContext(ClientContext);
  if (context === undefined) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
};

interface ClientProviderProps {
  children: React.ReactNode;
}

const STORAGE_KEY = 'selldo_ai_selected_client';
const SESSION_KEY = 'selldo_ai_client_session';

export const ClientProvider: React.FC<ClientProviderProps> = ({ children }) => {
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [searchInfo, setSearchInfo] = useState<SearchInfo | null>(null);
  const [hasFetched, setHasFetched] = useState(false);
  const initialFetchRef = useRef(false);

  // Load client from localStorage on mount
  useEffect(() => {
    const loadStoredClient = () => {
      try {
        const storedClient = localStorage.getItem(STORAGE_KEY);
        const sessionClient = sessionStorage.getItem(SESSION_KEY);
        
        if (storedClient) {
          const client = JSON.parse(storedClient);
          setSelectedClient(client);
        } else if (sessionClient) {
          const client = JSON.parse(sessionClient);
          setSelectedClient(client);
        }
      } catch (error) {
        console.error('Error loading stored client:', error);
        // Clear corrupted data
        localStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(SESSION_KEY);
      }
    };

    loadStoredClient();
  }, []);

  // Save client to storage when it changes
  const saveClientToStorage = useCallback((client: Client | null) => {
    try {
      if (client) {
        // Save to both localStorage (persistent) and sessionStorage (session-based)
        localStorage.setItem(STORAGE_KEY, JSON.stringify(client));
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(client));
      } else {
        // Clear both storages when client is null
        localStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(SESSION_KEY);
      }
    } catch (error) {
      console.error('Error saving client to storage:', error);
    }
  }, []);

  const selectClient = useCallback((client: Client) => {
    console.log('selectClient called with:', client);
    setSelectedClient(client);
    saveClientToStorage(client);
    console.log('Client selected and saved to storage');
  }, [saveClientToStorage]);

  const clearSelectedClient = useCallback(() => {
    setSelectedClient(null);
    saveClientToStorage(null);
  }, [saveClientToStorage]);

  // Reset entire context (for logout)
  const resetContext = useCallback(() => {
    setSelectedClient(null);
    setClients([]);
    setPagination(null);
    setSearchInfo(null);
    setHasFetched(false);
    setError(null);
    setLoading(false);
    initialFetchRef.current = false;
    saveClientToStorage(null);
  }, [saveClientToStorage]);

  const fetchClients = useCallback(async (page: number = 1, query: string = '') => {
    if (loading) return;

    try {
      console.log('fetchClients called with page:', page, 'query:', query);
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        return;
      }

      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '10',
        ...(query && { query })
      });

      console.log('Fetching clients from:', `${API_CONFIG.FULL_BASE_URL}/support_clients?${params}`);
      const response = await axios.get(`${API_CONFIG.FULL_BASE_URL}/support_clients?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Clients response:', response.data);
      if (response.data.success) {
        const { tenants, pagination: paginationData, search } = response.data.data;
        
        if (page === 1) {
          setClients(tenants);
        } else {
          setClients(prev => [...prev, ...tenants]);
        }
        
        setPagination(paginationData);
        setSearchInfo(search);
        setHasFetched(true);
        console.log('Clients set:', tenants.length, 'clients');
      } else {
        setError(response.data.error || 'Failed to fetch clients');
      }
    } catch (err: any) {
      console.error('Error fetching clients:', err);
      
      // Handle specific error cases
      if (err.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
        // Clear stored client on auth failure
        clearSelectedClient();
      } else if (err.response?.status === 403) {
        setError('Access denied. You do not have permission to view clients.');
      } else if (err.response?.status >= 500) {
        setError('Server error. Please try again later.');
      } else {
        setError(err.response?.data?.error || 'Failed to fetch clients');
      }
    } finally {
      setLoading(false);
    }
  }, [loading, clearSelectedClient]);

  const searchClients = useCallback(async (query: string) => {
    await fetchClients(1, query);
  }, [fetchClients]);

  const loadMoreClients = useCallback(async () => {
    if (pagination && pagination.has_next_page) {
      await fetchClients(pagination.current_page + 1, searchInfo?.query || '');
    }
  }, [fetchClients, pagination, searchInfo]);

  const retryFetchClients = useCallback(async () => {
    setError(null); // Clear error before retrying
    await fetchClients(1, '');
  }, [fetchClients]);

  // Auto-fetch clients for support managers (only once)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && !hasFetched && !loading && !initialFetchRef.current && !error) {
      initialFetchRef.current = true;
      fetchClients();
    }
  }, [hasFetched, loading, error]); // Added error to dependencies to prevent retry on error

  // Clear client selection when user logs out (token is removed)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'token' && !e.newValue) {
        resetContext();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [resetContext]);

  const value: ClientContextType = {
    selectedClient,
    clients,
    loading,
    error,
    pagination,
    searchInfo,
    hasFetched,
    selectClient,
    clearSelectedClient,
    fetchClients,
    searchClients,
    loadMoreClients,
    retryFetchClients,
    resetContext
  };

  return (
    <ClientContext.Provider value={value}>
      {children}
    </ClientContext.Provider>
  );
};
